import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { CrystalSpireAnalysisData, CrystalSpireFightRow, CrystalSpireHealerResult } from '../types';
import TbcErrorBanner from '../tbcAnalyzer/components/TbcErrorBanner';
import TbcFightBreakdownTable, { TbcBreakdownColumn } from '../tbcAnalyzer/components/TbcFightBreakdownTable';
import TbcLogUrlPhase from '../tbcAnalyzer/components/TbcLogUrlPhase';
import TbcPhaseHeader from '../tbcAnalyzer/components/TbcPhaseHeader';
import TbcSelectionPhase from '../tbcAnalyzer/components/TbcSelectionPhase';
import { useTbcAnalyzerSession } from '../tbcAnalyzer/useTbcAnalyzerSession';
import { TbcReportRouteProps, useTbcReportRoute } from '../tbcAnalyzer/useTbcReportRoute';
import { fightFilterLabel, formatDuration, formatNumber, isBossWipe } from '../tbcAnalyzer/utils';
import HelpTip from './HelpTip';

const ANALYZE_TIMEOUT_MS = 10 * 60 * 1000;

function perSecond(amount: number, durationSeconds: number): number {
    return durationSeconds > 0 ? amount / durationSeconds : 0;
}

function formatPercent(fraction: number, decimals = 1): string {
    return `${formatNumber(fraction * 100, decimals)}%`;
}

function formatDelta(simulated: number, actual: number): string {
    if (actual <= 0) {
        return '—';
    }
    const delta = (simulated - actual) / actual;
    return `${delta >= 0 ? '+' : ''}${formatPercent(delta)}`;
}

function overhealFraction(tally: { simulatedRaw: number; simulatedEffective: number }): number {
    return tally.simulatedRaw > 0 ? 1 - tally.simulatedEffective / tally.simulatedRaw : 0;
}

function sumFights(fights: CrystalSpireFightRow[], value: (fight: CrystalSpireFightRow) => number): number {
    return fights.reduce((sum, fight) => sum + value(fight), 0);
}

const VALIDATION_COLUMNS: TbcBreakdownColumn<CrystalSpireFightRow>[] = [
    {
        key: 'dur',
        header: 'Dur',
        render: (fight) => (
            <>
                {formatDuration(fight.durationSeconds)}
                {isBossWipe(fight) && <span className="fight-type-badge">wipe</span>}
            </>
        ),
        aggregate: (fights) => formatDuration(sumFights(fights, (fight) => fight.durationSeconds)),
    },
    {
        key: 'eligible',
        header: 'Heals < 50%',
        render: (fight) => formatNumber(fight.eligibleHeals),
        aggregate: (fights) => formatNumber(sumFights(fights, (fight) => fight.eligibleHeals)),
    },
    {
        key: 'procs',
        header: 'Real procs',
        render: (fight) => formatNumber(fight.actualProcs),
        aggregate: (fights) => formatNumber(sumFights(fights, (fight) => fight.actualProcs)),
    },
    {
        key: 'sim',
        header: 'Simulated HPS',
        render: (fight) => formatNumber(perSecond(fight.simulatedEffective, fight.durationSeconds), 1),
        aggregate: (fights) =>
            formatNumber(
                perSecond(
                    sumFights(fights, (fight) => fight.simulatedEffective),
                    sumFights(fights, (fight) => fight.durationSeconds)
                ),
                1
            ),
    },
    {
        key: 'actual',
        header: 'Actual HPS',
        render: (fight) => formatNumber(perSecond(fight.actualEffective, fight.durationSeconds), 1),
        aggregate: (fights) =>
            formatNumber(
                perSecond(
                    sumFights(fights, (fight) => fight.actualEffective),
                    sumFights(fights, (fight) => fight.durationSeconds)
                ),
                1
            ),
    },
    {
        key: 'delta',
        header: 'Sim vs actual',
        render: (fight) => formatDelta(fight.simulatedEffective, fight.actualEffective),
        aggregate: (fights) =>
            formatDelta(
                sumFights(fights, (fight) => fight.simulatedEffective),
                sumFights(fights, (fight) => fight.actualEffective)
            ),
    },
];

const CrystalSpireAnalyzer: React.FC<TbcReportRouteProps> = ({ basePath, reportId }) => {
    const session = useTbcAnalyzerSession();
    const [selectedPlayerNames, setSelectedPlayerNames] = useState<string[]>([]);
    const [result, setResult] = useState<CrystalSpireAnalysisData | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const reportRoute = useTbcReportRoute<CrystalSpireAnalysisData, string[]>({
        basePath,
        reportId,
        session,
        onRestore: (restored, restoredPlayerNames) => {
            setResult(restored);
            setSelectedPlayerNames(restoredPlayerNames ?? []);
        },
        onLeave: () => {
            setResult(null);
        },
    });

    // Every healer by default: the point is comparing who would get the most out of it.
    useEffect(() => {
        if (session.players.length === 0) {
            return;
        }
        const validNames = new Set(session.players.map((player) => player.name));
        setSelectedPlayerNames((current) => {
            const kept = current.filter((name) => validNames.has(name));
            if (kept.length > 0) {
                return kept;
            }
            return session.players.map((player) => player.name);
        });
    }, [session.players]);

    const handleTogglePlayerName = (name: string) => {
        setSelectedPlayerNames((current) => {
            if (current.indexOf(name) !== -1) {
                return current.filter((entry) => entry !== name);
            }
            return [...current, name];
        });
    };

    const handleProcessAnalysis = async () => {
        if (selectedPlayerNames.length === 0) {
            session.setError('Please select at least one healer for analysis');
            return;
        }
        if (session.loadedLogUrls.length === 0) {
            session.setError('No logs loaded');
            return;
        }
        if (session.bosses.length > 0 && session.selectedEncounterIds.length === 0) {
            session.setError('Please select at least one boss');
            return;
        }

        session.setLoading(true);
        session.setError(null);
        setResult(null);
        const wclProgressId = session.beginWclProgress();

        try {
            const orderedNames = session.players
                .map((player) => player.name)
                .filter((name) => selectedPlayerNames.indexOf(name) !== -1);
            const response = await axios.post(
                '/api/attendance/analyze-crystal-spire',
                {
                    ...session.sharedRequestBody,
                    selectedPlayerNames: orderedNames,
                    wclProgressId,
                },
                { timeout: ANALYZE_TIMEOUT_MS }
            );
            setResult(response.data);
            session.setPhase('results');
            reportRoute.publish(response.data, selectedPlayerNames);
        } catch (err: any) {
            console.error('Error analyzing Crystal Spire of Karabor:', err);
            session.setError(
                err.response?.data?.error || err.response?.data?.details || 'Failed to analyze logs. Please try again.'
            );
        } finally {
            session.endWclProgress();
            session.setLoading(false);
        }
    };

    const handleBackToUrl = () => {
        session.handleBackToUrl();
        setResult(null);
        setSelectedPlayerNames([]);
    };

    const rankedHealers = useMemo(
        () => [...(result?.healers ?? [])].sort((a, b) => b.simulatedHps - a.simulatedHps),
        [result]
    );
    const wielders = rankedHealers.filter((healer) => healer.equipped !== null);
    const filterLabel = result ? fightFilterLabel(result.fightFilter, session.bosses, result.fightFilterLabel) : '';

    const analyzeButtonLabel =
        selectedPlayerNames.length <= 1
            ? 'Simulate Crystal Spire'
            : `Simulate Crystal Spire (${selectedPlayerNames.length} healers)`;

    const spireHelp = (
        <HelpTip label="How the Crystal Spire simulation works" wide>
            <h4>The item</h4>
            <p>
                Crystal Spire of Karabor: a direct heal on a target below 50% health also heals it for 180–220. It
                always procs, doesn’t scale with +healing, and can crit.
            </p>
            <h4>How it’s simulated</h4>
            <ul>
                <li>
                    Every direct heal in the log is checked against the target’s health <em>before</em> the heal. HoT
                    ticks never count, and neither do Lifebloom’s bloom, Prayer of Mending or Earth Shield.
                </li>
                <li>
                    Warcraft Logs only gives health as a %, so each target’s max HP is estimated from the log itself.
                </li>
                <li>
                    Effective healing is worked out heal by heal: the proc can only fill what the target was still
                    missing after the heal that triggered it.
                </li>
                <li>
                    Shamans and priests get +10% on the proc (Purification, Spiritual Healing; druids’ Gift of Nature does not apply). It
                    crits at their direct-heal crit rate, minus heal-only crit talents.
                </li>
                <li>Warlocks take 26% more from it (Fel Armor with Demonic Aegis).</li>
            </ul>
            <h4>Validation</h4>
            <p>
                For anyone who had the mace equipped, the simulation is compared with their real procs on the same
                fights. Checked on 6 wielders (2 shamans, a priest, 3 druids): within ±5% for five of them, one
                druid 9% low. Paladins are untested.
            </p>
        </HelpTip>
    );

    return (
        <div className="item-set-calculator">
            {session.phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        Crystal Spire of Karabor
                        {spireHelp}
                    </h2>
                    <p className="analyzer-description">
                        Simulated HPS from the mace’s below-50% proc for every healer, checked against real procs from
                        anyone who had it equipped.
                    </p>
                </>
            )}

            <TbcErrorBanner error={session.error} />

            {session.phase === 'url' && !reportRoute.restoring && (
                <TbcLogUrlPhase
                    logUrlsText={session.logUrlsText}
                    loading={session.loading}
                    onLogUrlsChange={session.setLogUrlsText}
                    onLoadLogs={() => {
                        void session.handleLoadLogs();
                    }}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'selection' && (
                <TbcSelectionPhase
                    loadedLogUrls={session.loadedLogUrls}
                    players={session.players}
                    bosses={session.bosses}
                    selectedPlayerNames={selectedPlayerNames}
                    fightFilter={session.fightFilter}
                    selectedEncounterIds={session.selectedEncounterIds}
                    minDurationSeconds={session.minDurationSeconds}
                    loading={session.loading}
                    analyzeButtonLabel={analyzeButtonLabel}
                    showMinDurationFilter
                    minDurationHelpText="Fights shorter than this are left out."
                    onTogglePlayerName={handleTogglePlayerName}
                    onSelectAllPlayers={() => setSelectedPlayerNames(session.players.map((player) => player.name))}
                    onClearPlayers={() => setSelectedPlayerNames([])}
                    onFightFilterChange={session.setFightFilter}
                    onToggleEncounterId={session.toggleEncounterId}
                    onToggleAllEncounterIds={session.toggleAllEncounterIds}
                    onToggleRaidEncounterIds={session.toggleRaidEncounterIds}
                    onMinDurationChange={session.setMinDurationSeconds}
                    onAnalyze={() => {
                        void handleProcessAnalysis();
                    }}
                    onBack={handleBackToUrl}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'results' && result && (
                <div className="results-phase">
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        title="Crystal Spire of Karabor"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={spireHelp}
                    />

                    <p className="results-status">
                        {rankedHealers.length} healer{rankedHealers.length === 1 ? '' : 's'}
                        {filterLabel ? ` · ${filterLabel}` : ''}
                        {result.duplicatesRemoved > 0
                            ? ` · −${result.duplicatesRemoved} duplicate pull${
                                  result.duplicatesRemoved === 1 ? '' : 's'
                              }`
                            : ''}
                        {result.missingPlayerNames.length > 0
                            ? ` · missing ${result.missingPlayerNames.join(', ')}`
                            : ''}
                    </p>

                    <div ref={resultsRef}>
                        <SimulationTable healers={rankedHealers} />
                        {wielders.map((healer) => (
                            <WielderValidation key={healer.name} healer={healer} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SimulationTable: React.FC<{ healers: CrystalSpireHealerResult[] }> = ({ healers }) => (
    <div className="table-container" style={{ marginTop: '20px' }}>
        <table className="breakdown-table">
            <thead>
                <tr>
                    <th>Healer</th>
                    <th>Active</th>
                    <th>Direct heals</th>
                    <th>Below 50%</th>
                    <th>Proc crit</th>
                    <th>Overheal</th>
                    <th>Simulated HPS</th>
                    <th>Actual HPS</th>
                </tr>
            </thead>
            <tbody>
                {healers.length === 0 && (
                    <tr>
                        <td colSpan={8}>No fights matched the selected filter.</td>
                    </tr>
                )}
                {healers.map((healer) => (
                    <tr key={healer.name}>
                        <td>
                            {healer.name}
                            <span className="boss-group-count"> {healer.class}</span>
                        </td>
                        <td>
                            {formatDuration(healer.durationSeconds)} · {healer.fightsProcessed} fights
                        </td>
                        <td>{formatNumber(healer.directHeals)}</td>
                        <td>
                            {formatNumber(healer.eligibleHeals)}
                            {healer.directHeals > 0 && (
                                <span className="boss-group-count">
                                    {' '}
                                    ({formatPercent(healer.eligibleHeals / healer.directHeals, 0)})
                                </span>
                            )}
                        </td>
                        <td>{formatPercent(healer.critChance)}</td>
                        <td>{formatPercent(overhealFraction(healer))}</td>
                        <td>
                            <strong>{formatNumber(healer.simulatedHps, 1)}</strong>
                        </td>
                        <td>
                            {healer.equipped ? (
                                <>
                                    {formatNumber(healer.equipped.actualHps, 1)}
                                    <span className="boss-group-count">
                                        {' '}
                                        (sim {formatNumber(healer.equipped.simulatedHps, 1)})
                                    </span>
                                </>
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const WielderValidation: React.FC<{ healer: CrystalSpireHealerResult }> = ({ healer }) => {
    const equipped = healer.equipped;
    const fights = useMemo(() => healer.fights.filter((fight) => fight.equipped), [healer.fights]);
    if (!equipped) {
        return null;
    }

    return (
        <>
            <h3 style={{ marginTop: '32px' }}>
                Validation: {healer.name} ({equipped.fights} fight{equipped.fights === 1 ? '' : 's'} with the mace)
            </h3>
            <div className="results-summary">
                <div className="summary-card">
                    <h4>HPS from the proc</h4>
                    <div className="summary-stats">
                        <div className="stat">
                            <span className="stat-label">Simulated:</span>
                            <span className="stat-value">{formatNumber(equipped.simulatedHps, 1)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Actual:</span>
                            <span className="stat-value">{formatNumber(equipped.actualHps, 1)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Difference:</span>
                            <span className="stat-value">
                                {formatDelta(equipped.simulatedEffective, equipped.actualEffective)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="summary-card">
                    <h4>Procs</h4>
                    <div className="summary-stats">
                        <div className="stat">
                            <span className="stat-label">Predicted (heals below 50%):</span>
                            <span className="stat-value">{formatNumber(equipped.eligibleHeals)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Actual:</span>
                            <span className="stat-value">{formatNumber(equipped.actualProcs)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Predicted on the right heal:</span>
                            <span className="stat-value">{formatNumber(equipped.matchedProcs)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Crit rate (actual / used):</span>
                            <span className="stat-value">
                                {equipped.actualProcs > 0
                                    ? formatPercent(equipped.actualCrits / equipped.actualProcs)
                                    : '—'}{' '}
                                / {formatPercent(healer.critChance)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <TbcFightBreakdownTable
                fights={fights}
                columns={VALIDATION_COLUMNS}
                rowKey={(fight) => `${fight.reportId}-${fight.fightId}`}
            />
        </>
    );
};

export default CrystalSpireAnalyzer;
